import {
  ConnectAccountManagement,
  ConnectNotificationBanner,
  ConnectTaxRegistrations,
  ConnectTaxSettings,
} from "@stripe/react-connect-js";
import { createFileRoute } from "@tanstack/react-router";

import { PageWrapper } from "~/components/page-wrapper";
import { ConnectProvider } from "~/components/stripe/connect-provider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

export const Route = createFileRoute(
  "/_authenticated/settings/account-management"
)({
  component: AccountManagementPage,
  pendingComponent: () => (
    <PageWrapper
      description="Manage your business information and payout settings"
      title="Account Management"
    >
      <div className="bg-card animate-pulse rounded-xl border">
        <div className="space-y-6 p-6">
          <div className="bg-muted h-6 w-48 rounded-md" />
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="bg-muted h-4 w-32 rounded" />
              <div className="bg-muted h-10 w-full rounded-md" />
            </div>
            <div className="space-y-4">
              <div className="bg-muted h-4 w-32 rounded" />
              <div className="bg-muted h-10 w-full rounded-md" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-muted h-4 w-40 rounded" />
            <div className="bg-muted h-10 w-full rounded-md" />
          </div>
          <div className="flex justify-end">
            <div className="bg-muted h-10 w-32 rounded-md" />
          </div>
        </div>
      </div>
    </PageWrapper>
  ),
});

function AccountManagementPage() {
  const { organization } = useActiveOrganization();

  if (!organization) {
    return null;
  }

  return (
    <PageWrapper
      description="Update your business details, bank account information, and payout schedule"
      title="Account Management"
    >
      <div className="space-y-6">
        <ConnectProvider organizationId={organization.id}>
          <ConnectNotificationBanner />
        </ConnectProvider>

        {/* Main Account Management */}
        <ConnectProvider
          className="bg-card overflow-hidden rounded-xl border p-6"
          organizationId={organization.id}
        >
          <ConnectAccountManagement />
        </ConnectProvider>

        {/* Tax Compliance Section */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Compliance</CardTitle>
            <CardDescription>
              Manage your tax settings and registrations for 1099 reporting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tax Settings */}
            <div>
              <h3 className="mb-3 text-sm font-medium">Tax Settings</h3>
              <ConnectProvider
                className="overflow-hidden rounded-lg border p-4"
                organizationId={organization.id}
              >
                <ConnectTaxSettings />
              </ConnectProvider>
            </div>

            {/* Tax Registrations */}
            <div>
              <h3 className="mb-3 text-sm font-medium">Tax Registrations</h3>
              <ConnectProvider
                className="overflow-hidden rounded-lg border p-4"
                organizationId={organization.id}
              >
                <ConnectTaxRegistrations />
              </ConnectProvider>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
